import { Loader2 } from 'lucide-react';
import type {
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import type { LoginFormValues } from '@/features/auth/schemas/login.schema';

interface LoginFormProps {
  register: UseFormRegister<LoginFormValues>;
  errors: FieldErrors<LoginFormValues>;
  isSubmitting: boolean;
  isValid: boolean;
  isLocked: boolean;
  handleSubmit: UseFormHandleSubmit<LoginFormValues>;
  onSubmit: (data: LoginFormValues) => void;
}

export function LoginForm({
  register,
  errors,
  isSubmitting,
  isValid,
  isLocked,
  handleSubmit,
  onSubmit,
}: LoginFormProps) {
  const { t } = useTranslation(['auth', 'validation']);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="login-page__form"
    >
      <div className="login-page__field">
        <label htmlFor="loginEmail" className="login-page__label">
          {t('auth:login.email')}
        </label>
        <div className="login-page__input-wrap">
          <input
            id="loginEmail"
            type="email"
            autoComplete="email"
            placeholder={t('auth:login.emailPlaceholder')}
            disabled={isLocked}
            {...register('email')}
            className="login-page__input"
          />
        </div>
        {errors.email?.message && (
          <p className="login-page__error">{t(errors.email.message)}</p>
        )}
      </div>

      <div className="login-page__field">
        <label htmlFor="loginPassword" className="login-page__label">
          {t('auth:login.password')}
        </label>
        <div className="login-page__input-wrap">
          <input
            id="loginPassword"
            type="password"
            autoComplete="current-password"
            placeholder={t('auth:login.passwordPlaceholder')}
            disabled={isLocked}
            {...register('password')}
            className="login-page__input"
          />
        </div>
        {errors.password?.message && (
          <p className="login-page__error">{t(errors.password.message)}</p>
        )}
      </div>

      {/* Root error is pre-translated by the page handler (count interpolation,
          locked-state copy, etc.) so it renders verbatim here. */}
      {errors.root?.message && (
        <p className="login-page__error">{errors.root.message}</p>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        disabled={!isValid || isSubmitting || isLocked}
        className="login-page__submit"
      >
        {isSubmitting && (
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />
        )}
        {t('auth:login.submit')}
      </Button>
    </form>
  );
}
